/**
 * useConfirmDialog Composable
 *
 * Provides a promise-based API for showing a ConfirmDialog and awaiting
 * the user's decision. Ideal for async flows like `onBeforeRouteLeave`
 * where you need to await user confirmation before proceeding.
 *
 * @example
 * ```ts
 * const { confirmState, requestConfirm, handleConfirm, handleCancel } = useConfirmDialog();
 *
 * // In a route guard or async handler:
 * const confirmed = await requestConfirm({
 *   title: 'Discard Changes?',
 *   message: 'All unsaved data will be lost.',
 *   confirmLabel: 'Discard',
 *   confirmSeverity: 'danger',
 * });
 * if (!confirmed) return;
 * ```
 *
 * ```vue
 * <!-- Use interceptClose to make the dialog persistent (Escape / mask
 *      click won't bypass the buttons).  Route @update:visible through
 *      handleVisibleUpdate so an accidental hide still resolves the promise. -->
 * <DialogModal
 *   :visible="confirmState.visible"
 *   interceptClose
 *   :show-close="false"
 *   @update:visible="handleVisibleUpdate"
 *   ...
 * />
 * ```
 */
import { reactive } from 'vue';

export type ConfirmSeverity = 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger' | 'contrast';

export interface ConfirmDialogOptions {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmSeverity?: ConfirmSeverity;
}

export interface ConfirmDialogState {
  visible: boolean;
  props: ConfirmDialogOptions;
}

export function useConfirmDialog() {
  const confirmState = reactive<ConfirmDialogState>({
    visible: false,
    props: {},
  });

  let resolvePromise: ((value: boolean) => void) | null = null;

  /**
   * Show the confirmation dialog and return a promise that resolves
   * to `true` if confirmed, `false` if cancelled.
   *
   * If a previous request is still pending it is auto-cancelled (resolved
   * with `false`) before the new dialog is shown, so no caller can get
   * stuck awaiting forever.
   */
  function requestConfirm(options: ConfirmDialogOptions = {}): Promise<boolean> {
    // Auto-cancel any in-flight request so it doesn't leak.
    if (resolvePromise) {
      resolvePromise(false);
      resolvePromise = null;
    }

    confirmState.props = { ...options };
    confirmState.visible = true;

    return new Promise<boolean>((resolve) => {
      resolvePromise = resolve;
    });
  }

  function handleConfirm() {
    confirmState.visible = false;
    confirmState.props = {};
    resolvePromise?.(true);
    resolvePromise = null;
  }

  function handleCancel() {
    confirmState.visible = false;
    confirmState.props = {};
    resolvePromise?.(false);
    resolvePromise = null;
  }

  /**
   * Safety-net handler for `@update:visible`.  If the dialog is hidden
   * externally (e.g. PrimeVue's internal Escape / mask click leaks through)
   * this ensures the pending promise resolves with `false` instead of
   * hanging forever.
   *
   * Usage: `@update:visible="handleVisibleUpdate"`
   */
  function handleVisibleUpdate(value: boolean) {
    if (!value && confirmState.visible) {
      handleCancel();
      return;
    }
    confirmState.visible = value;
  }

  return {
    confirmState,
    requestConfirm,
    handleConfirm,
    handleCancel,
    handleVisibleUpdate,
  };
}
